import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2, CheckCircle2, MoreVertical, Check, Home, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { updateCustomers } from "@/services/auth";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { verifyUPIID } from "@/services/customer";
import { useTranslation } from "react-i18next";

const BankDetails = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { data: customer } = useSelector((state) => state.customer);
    const bankList = customer?.customer?.bankInfo || [];
    const hasBankInfo = bankList.length > 0;
    const savedBank = bankList[0];

    const [loading, setLoading] = useState(false);
    const [ifscLoading, setIfscLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [menuOpen, setMenuOpen] = useState(null);
    const [bankName, setBankName] = useState("");
    const [ifscVerified, setIfscVerified] = useState(false);
    const [accountHolderName, setAccountHolderName] = useState("");

    const [formData, setFormData] = useState({
        ifscCode: "",
        accountNumber: "",
        confirmAccountNumber: "",
    });

    useEffect(() => {
        const customerCode = localStorage.getItem("customercode");
        if (!customer) dispatch(fetchCustomerData(customerCode));
    }, []);

    const fetchBankName = async (ifsc) => {
        try {
            setIfscLoading(true);
            setIfscVerified(false);
            setBankName("");
            const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
            const data = await res.json();
            if (data?.BANK) {
                setBankName(data.BANK);
                setIfscVerified(true);
            } else {
                toast({
                    title: t("common.failed"),
                    description: t("bank.invalidIfsc"),
                    variant: "destructive",
                });
            }
        } catch {
            toast({
                title: t("common.failed"),
                description: t("bank.fetchError"),
                variant: "destructive",
            });
        }
        setIfscLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedValue = name === "ifscCode" ? value.toUpperCase() : value;
        setFormData({ ...formData, [name]: updatedValue });
        if (name === "ifscCode") {
            setIfscVerified(false);
            setBankName("");
            if (updatedValue.length === 11) fetchBankName(updatedValue);
        }
    };

    const handleSubmit = async () => {
        if (!formData.ifscCode || !formData.accountNumber || !formData.confirmAccountNumber) {
            toast({
                title: t("common.failed"),
                description: t("bank.fillAllFields"),
                variant: "destructive",
            });
            return;
        }
        if (formData.accountNumber !== formData.confirmAccountNumber) {
            toast({
                title: t("common.failed"),
                description: t("bank.accountMismatch"),
                variant: "destructive",
            });
            return;
        }
        if (!bankName) {
            toast({
                title: t("common.failed"),
                description: t("bank.invalidIfsc"),
                variant: "destructive",
            });
            return;
        }

        setLoading(true);
        const token = JSON.parse(localStorage.getItem("token_gen"));
        const customerCode = localStorage.getItem("customercode");
        let holderName = "";
        try {
            const verifyRes = await verifyUPIID(token?.access_token, {
                verification_type: "auto",
                accountNumber: formData.accountNumber,
                accountIfsc: formData.ifscCode,
                auto_priority: ["pennyless", "penny_drop", "paisa_drop"],
                consent: true,
            });
            if (verifyRes?.data?.data?.failure_reason === "INVALID ACCOUNT") {
                setLoading(false);
                toast({
                    title: t("common.failed"),
                    description: t("bank.verificationFailed"),
                    variant: "destructive",
                });
                return;
            }
            holderName = verifyRes?.data?.data?.account_holder_name || "";
            setAccountHolderName(holderName);
        } catch {
            setLoading(false);
            toast({
                title: t("common.failed"),
                description: t("bank.verificationError"),
                variant: "destructive",
            });
            return;
        }

        const newBank = {
            ifscCode: formData.ifscCode,
            bankName,
            accountNumber: formData.accountNumber,
            accountHolderName: holderName,
            nickname: holderName || bankName,
            isDefault: bankList.length === 0,
        };

        const updatedBankList = [...bankList, newBank];

        await updateCustomers(token?.access_token, customerCode, {
            bankInfo: updatedBankList,
            upiIdVerified: true,
        });
        toast({
            title: t("common.success"),
            description: isEditMode
                ? t("bank.updateSuccess")
                : t("bank.addSuccess"),
        });

        await dispatch(fetchCustomerData(customerCode));
        setLoading(false);
        setShowForm(false);
        setFormData({ ifscCode: "", accountNumber: "", confirmAccountNumber: "" });
        setBankName("");
        setIfscVerified(false);
        setAccountHolderName("");
    };

    const handleDelete = async (indexToDelete) => {
        setLoading(true);

        const token = JSON.parse(localStorage.getItem("token_gen"));
        const customerCode = localStorage.getItem("customercode");

        const wasDefault = bankList[indexToDelete]?.isDefault;

        let updatedList = bankList
            .filter((_, i) => i !== indexToDelete)
            .map((bank) => ({
                ifscCode: bank.ifscCode,
                bankName: bank.bankName,
                accountNumber: bank.accountNumber,
                accountHolderName: bank.accountHolderName,
                nickname: bank.nickname,
                isDefault: bank.isDefault,
            }));

        if (wasDefault && updatedList.length > 0) {
            updatedList = updatedList.map((bank, i) => ({
                ...bank,
                isDefault: i === 0,
            }));
        }

        await updateCustomers(token?.access_token, customerCode, {
            bankInfo: updatedList,
            upiIdVerified: updatedList.length > 0,
        });

        toast({
            title: t("common.success"),
            description: t("bank.deleted")
        });
        await dispatch(fetchCustomerData(customerCode));

        setLoading(false);
        setMenuOpen(null);
    };

    const handleSetDefault = async (indexToDefault) => {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem("token_gen"));
        const customerCode = localStorage.getItem("customercode");

        const updatedList = bankList.map((bank, i) => ({
            ...bank,
            isDefault: i === indexToDefault,
        }));

        await updateCustomers(token?.access_token, customerCode, { bankInfo: updatedList });
        toast({
            title: t("common.success"),
            description: t("bank.defaultUpdated")
        });
        await dispatch(fetchCustomerData(customerCode));
        setLoading(false);
        setMenuOpen(null);
    };

    const defaultBank = bankList.find((b) => b.isDefault) || bankList[0];

    return (
        <div className="min-h-screen bg-[#F5F6FA] flex justify-center">

            {/* Loader */}
            {loading && (
                <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
                    <Loader2 className="animate-spin h-10 w-10 text-[#1A1F36]" />
                </div>
            )}

            <div className="w-full max-w-md bg-white min-h-screen md:rounded-3xl md:shadow-lg">

                {/* HEADER */}
                <div className="relative flex items-center justify-center border-b border-gray-100 px-4 py-4">
                    <button
                        onClick={() => {
                            if (showForm) {
                                setShowForm(false);
                                setFormData({ ifscCode: "", accountNumber: "", confirmAccountNumber: "" });
                                setBankName("");
                                setIfscVerified(false);
                                setAccountHolderName("");
                            } else {
                                navigate(-1);
                            }
                        }}
                        className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full"
                        style={{ backgroundColor: "#1A1F3610" }}
                    >
                        <ArrowLeft className="w-5 h-5 text-[#1A1F36]" />
                    </button>

                    <p className="text-[15px] font-semibold text-[#1A1F36]">
                        {showForm ? t("bank.addAnotherAccount") : t("bank.bankDetails")}
                    </p>

                    <button
                        onClick={() => navigate("/home")}
                        className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full"
                        style={{ backgroundColor: "#1A1F3610" }}
                    >
                        <Home className="w-5 h-5 text-[#1A1F36]" />
                    </button>
                </div>

                <div className="px-4 py-5 space-y-4">

                    {showForm || !hasBankInfo ? (
                        <>
                            <div className="bg-white rounded-2xl px-5 py-5 shadow-sm mx-1 space-y-4">

                                {/* IFSC */}
                                <div>
                                    <p className="text-[13px] font-medium text-[#1A1F36] mb-1.5">
                                        {t("bank.ifscCode")}
                                    </p>

                                    <div className="relative">
                                        <input
                                            name="ifscCode"
                                            value={formData.ifscCode}
                                            onChange={handleChange}
                                            placeholder={t("bank.ifscPlaceholder")}
                                            maxLength={11}
                                            className="w-full border-[1.5px] border-[rgb(210,215,230)] rounded-xl px-4 py-3 text-[14px] text-[#1A1F36] outline-none focus:border-[#1A1F36] placeholder:text-[rgb(150,160,180)]"
                                        />

                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            {ifscLoading && <Loader2 className="animate-spin w-4 h-4 text-[#1A1F36]" />}
                                            {!ifscLoading && ifscVerified && <CheckCircle2 size={18} className="text-[#1A1F36]" />}
                                        </div>
                                    </div>

                                    {bankName && (
                                        <p className="text-[12px] text-[#1A1F36] mt-1 font-semibold flex items-center gap-1">
                                            <CheckCircle2 size={12} /> {bankName}
                                        </p>
                                    )}
                                </div>

                                {/* Account Number */}
                                <div>
                                    <p className="text-[13px] font-medium text-[#1A1F36] mb-1.5">
                                        {t("bank.accountNumber")}
                                    </p>
                                    <input
                                        name="accountNumber"
                                        value={formData.accountNumber}
                                        onChange={handleChange}
                                        placeholder={t("bank.enterAccountNumber")}
                                        className="w-full border-[1.5px] border-[rgb(210,215,230)] rounded-xl px-4 py-3 text-[14px] text-[#1A1F36] outline-none focus:border-[#1A1F36] placeholder:text-[rgb(150,160,180)]"
                                    />
                                </div>

                                {/* Confirm Account */}
                                <div>
                                    <p className="text-[13px] font-medium text-[#1A1F36] mb-1.5">
                                        {t("bank.reEnterAccount")}
                                    </p>
                                    <input
                                        name="confirmAccountNumber"
                                        value={formData.confirmAccountNumber}
                                        onChange={handleChange}
                                        placeholder={t("bank.confirmAccountNumber")}
                                        className="w-full border-[1.5px] border-[rgb(210,215,230)] rounded-xl px-4 py-3 text-[14px] text-[#1A1F36] outline-none focus:border-[#1A1F36] placeholder:text-[rgb(150,160,180)]"
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <button
                                onClick={handleSubmit}
                                disabled={!ifscVerified || !formData.accountNumber || !formData.confirmAccountNumber}
                                className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-50"
                                style={{ width: 'calc(100% - 8px)', marginLeft: '4px', backgroundColor: 'rgb(26,31,54)' }}
                            >
                                {t("bank.addAnotherAccount")}
                            </button>
                        </>
                    ) : (
                        <>
                            {bankList.map((bank, index) => (
                                <div key={index} className="bg-white rounded-2xl px-5 py-4 shadow-sm mx-1 relative">

                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1 flex-1">

                                            <p className="text-[13px] text-[#1A1F36]">
                                                <span className="font-semibold">{t("bank.accountName")}:</span> {bank?.accountHolderName || "—"}
                                            </p>

                                            <p className="text-[13px] text-[#1A1F36]">
                                                <span className="font-semibold">{t("bank.accountNumber")}:</span> {bank?.accountNumber}
                                            </p>

                                            <p className="text-[13px] text-[#1A1F36]">
                                                <span className="font-semibold">{t("bank.ifscCode")}:</span> {bank?.ifscCode}
                                            </p>

                                            <p className="text-[13px] text-[#1A1F36]">
                                                <span className="font-semibold">{t("bank.bankName")}:</span> {bank?.bankName}
                                            </p>

                                            {bank?.isDefault && (
                                                <div className="flex items-center gap-1 border border-[rgb(210,215,230)] rounded-lg px-2.5 py-1 w-fit mt-1">
                                                    <Check size={11} className="text-[#1A1F36]" />
                                                    <span className="text-[11px] font-semibold text-[#1A1F36]">{t("bank.default")}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="relative">
                                            <MoreVertical
                                                size={18}
                                                className="cursor-pointer text-gray-400"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setMenuOpen(menuOpen === index ? null : index);
                                                }}
                                            />
                                            {menuOpen === index && (
                                                <div className="absolute right-0 top-6 bg-white shadow-lg rounded-xl p-1.5 w-36 z-50 border border-gray-100">
                                                    {!bank?.isDefault && (
                                                        <p
                                                            onClick={(e) => { e.stopPropagation(); handleSetDefault(index); }}
                                                            className="px-3 py-2 text-[13px] hover:bg-gray-50 cursor-pointer rounded-lg text-[#2d5a2d] font-medium"
                                                        >
                                                            {t("bank.setDefault")}
                                                        </p>
                                                    )}
                                                    <p
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(index); }}
                                                        className="px-3 py-2 text-[13px] hover:bg-gray-50 cursor-pointer rounded-lg text-red-500"
                                                    >
                                                        {t("bank.delete")}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Transfer Button */}
                            <button
                                onClick={() => navigate("/upi-confirm", { state: { defaultBank } })}
                                className="w-full py-4 rounded-xl text-[15px] font-bold text-white"
                                style={{ width: 'calc(100% - 8px)', marginLeft: '4px', backgroundColor: 'rgb(26,31,54)' }}
                            >
                                {t("bank.transferAmount")}
                            </button>

                            {/* Add Account */}
                            <button
                                onClick={() => setShowForm(true)}
                                className="w-full py-4 rounded-xl text-[15px] font-semibold text-[#1A1F36] border-[1.5px] border-[rgb(210,215,230)] bg-white flex items-center justify-center gap-2"
                                style={{ width: 'calc(100% - 8px)', marginLeft: '4px' }}
                            >
                                <Plus size={16} />
                                {t("bank.addAnotherAccount")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BankDetails;